// app/customs/spreadcontrol/index.tsx
'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import axios from 'axios';
import * as Global from '@utils/global';
import * as AuthContext from '@/utils/authcontext';
import SpreadTable from '@/customs/spreadtable';
import ErrorMessage from '@/customs/errormessage';
import './index.css';

type SpreadMenuItem = {
  spread_menu_guid: string;
  spread_id: string;
  title: string;
  permission: string;
  badge_count_enabled?: boolean;
  base_filters?: Record<string, string>;
  ui_filters?: Record<string, string>;
  form_pkey: string;
  form_cursor_type?: string | Global.FormCursorType;  
  output_type?: string | Global.OutputType;  
};

type SpreadMenuGroup = {
  groupid: string;
  group_title: string;
  items: SpreadMenuItem[];
};

const getJstDateKey = () => (
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date())
);

const SpreadControl: React.FC = () => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const spreadMenuGuid = searchParams.get('spread_menu_guid');

  const [menuGroups, setMenuGroups] = useState<SpreadMenuGroup[]>([]);
  const [badgeCounts, setBadgeCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [menuDateKey, setMenuDateKey] = useState(getJstDateKey);
  const { authToken, employeeDomain } = AuthContext.useAuth();
  const previousMenuGuid = useRef(spreadMenuGuid);
  const badgeRequestId = useRef(0);
  // ② コンポーネント内の state にこれを追加（共通エラー状態を保持）
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchMenu = useCallback(async () => {
    const currentBadgeRequestId = ++badgeRequestId.current;
    setBadgeCounts({});
    if (!authToken || !employeeDomain) {
      setMenuGroups([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // const res = await axios.post<SpreadMenuGroup[]>(
      //   `${Global.getApiBaseUrl()}/spread/get_spreadmenu`,
      //   { 社員GUID: employeeGUID }
      // );

      const res = await axios.post<SpreadMenuGroup[]>(
        `${Global.getApiBaseUrl()}/spread/get_spreadmenu`,
        {
          base_token_interface: Global.createBaseTokenInterface(authToken, employeeDomain)
        }
      );

      setMenuGroups(res.data);
      setMenuDateKey(getJstDateKey());
      if (res.data.some(group => group.items.some(item => item.badge_count_enabled))) {
        void axios.post<Record<string, number>>(
          `${Global.getApiBaseUrl()}/spread/get_spreadmenu_badges`,
          { base_token_interface: Global.createBaseTokenInterface(authToken, employeeDomain) }
        ).then(badgeRes => {
          if (currentBadgeRequestId === badgeRequestId.current) setBadgeCounts(badgeRes.data);
        }).catch(err => {
          if (currentBadgeRequestId === badgeRequestId.current) {
            setErrorMessage(`メニュー件数取得エラー||${(err as Error)?.message || JSON.stringify(err)}`);
            console.error('メニュー件数取得エラー', err);
          }
        });
      }
    } catch (err) {
      setErrorMessage(`メニュー取得エラー||${(err as Error)?.message || JSON.stringify(err)}`);
      console.error("メニュー取得エラー", err);
    } finally {
      setLoading(false);
    }
  }, [authToken, employeeDomain]);

  useEffect(() => {
    fetchMenu();
  }, [fetchMenu]);

  useEffect(() => {
    const returnedToMenu = previousMenuGuid.current !== null && spreadMenuGuid === null;
    previousMenuGuid.current = spreadMenuGuid;
    if (returnedToMenu) fetchMenu();
  }, [spreadMenuGuid, fetchMenu]);

  useEffect(() => {
    const refreshIfDateChanged = () => {
      const currentDateKey = getJstDateKey();
      if (currentDateKey !== menuDateKey) {
        fetchMenu();
      }
    };

    const intervalId = window.setInterval(refreshIfDateChanged, 60 * 1000);
    window.addEventListener("focus", refreshIfDateChanged);
    window.addEventListener("pageshow", refreshIfDateChanged);
    document.addEventListener("visibilitychange", refreshIfDateChanged);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshIfDateChanged);
      window.removeEventListener("pageshow", refreshIfDateChanged);
      document.removeEventListener("visibilitychange", refreshIfDateChanged);
    };
  }, [fetchMenu, menuDateKey]);

  if (loading) return <div>読み込み中...</div>;

  // spread_menu_guid 優先で選択
  if (spreadMenuGuid) {
    let selected: SpreadMenuItem | undefined;
    for (const g of menuGroups) {
      selected = g.items.find(i => i.spread_menu_guid === spreadMenuGuid);
      if (selected) break;
    }

    if (!selected) return (
      <div className="spread-access-denied">
        <span>このメニューにアクセスする権限がありません</span>
        <button onClick={() => router.push('/')}>閉じる</button>
      </div>
    );

    return (
      <SpreadTable
        spread={selected.spread_id}
        base_filters={selected.base_filters}
        ui_filters={selected.ui_filters}
        form_pkey={selected.form_pkey}
        form_cursor_type={selected.form_cursor_type}
        output_type={selected.output_type}
      />
    );
  }

  return (
    <div className='spread-control-container'>
      {/* エラーメッセージ表示 */}
      {errorMessage && (
        <ErrorMessage
          message={errorMessage}
          onClose={() => setErrorMessage(null)}
          type="error"
        />
      )}
      {/* <h2>帳票メニュー</h2> */}
      {menuGroups.map(group => (
        <div key={group.groupid} className="spreadmenu-group">
          <div className="spread-grouptitle">
            <i className="fa-solid fa-layer-group" style={{marginRight:4}} />
            {group.group_title}
          </div>
          <div className="spreadmenu-container">
            {group.items.map((item) => (
              <button
                key={item.spread_menu_guid}
                className="spreadmenu-button"
                onClick={() => {
                  router.push(`?spread_menu_guid=${item.spread_menu_guid}`);
                }}
              >
                <div className="spreadmenu-title">{item.title}</div>
                <div className="spreadmenu-meta">
                  <span>{item.spread_id}：{item.permission}</span>
                  {badgeCounts[item.spread_menu_guid] > 0 && (
                    <span className="spreadmenu-badge" aria-label={`${badgeCounts[item.spread_menu_guid]}件`}>
                      {badgeCounts[item.spread_menu_guid]}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SpreadControl;
